<script lang="ts">
export default {
  name: 'AccountExposure',
};

const trendChart: Record<string, unknown> = {
  chart: {
    showToolTip: '0',
    caption: '',
    xaxisname: 'Effective Date',
    setAdaptiveYMin: '0',
    numberscalevalue: '1000,1000,1000',
    numberscaleunit: 'K,M,B',
    theme: 'cacs',
  },
};
</script>

<script setup lang="ts">
import { ref, computed, onBeforeMount, watch, inject } from 'vue';
import {
  ExposureAttribute,
  ExposureAttributeWithLookupKey,
  ExposureCategory,
  ExposureMetric,
  ExposureOption,
  ExposureProperty,
  ExposureValue,
  ExposureValueChangeEntry,
  ExposureValueEntry,
} from '@/types/AccountExposure';
import { accountService, referenceService } from '@/services/api';
import AccountExposureEntryModal from '@/views/account/AccountExposureEntryModal.vue';
import AccountExposureEditModal from '@/views/account/AccountExposureEditModal.vue';
import { LabelValue } from '@/types/label-value';
import { Account } from '@/types/Account';
import AccountExposureDeleteModal from './AccountExposureDeleteModal.vue';
import { CompareISODateStrings } from '@/utilities/Dates';
import { Events } from '@/store/EventBus';
import { IsUserAnAdmin, IsUserAClient } from '@/utilities/AuthUtils';
import { AppState } from '@/store';
import { DateTime, DateTimeOptions } from 'luxon';
import * as XLSX from 'xlsx';
import PayrollUploadModal from '@/components/PayrollUploadModal.vue';
import AccountExposureProperties from '@/views/account/AccountExposureProperties.vue';
import { Plus, Edit2, Trash2 } from '@lucide/vue';
import { useRoute, useRouter, RouteLocation } from 'vue-router';
import FileUploadModal from '@/components/FileUploadModal.vue';
import { filters } from '@/utilities/Filters';
import { Store } from 'vuex';
import { addCommas } from '@/utilities/Number';
import AppButton from '@/components/v2/AppButton.vue';
import DownloadButton from '@/components/v2/DownloadButton.vue';
import Toggle from '@/components/v2/Toggle.vue';
import { useVirtualizer } from '@tanstack/vue-virtual';
import Skeleton from '@/components/v2/Skeleton.vue';
import AppSelect from '@/components/v2/AppSelect.vue';
import { DownloadCSV } from '@/utilities/Download';

const props = defineProps<{
  account: Account;
}>();

const store = inject<Store<AppState>>('store');

const $route = useRoute();
const $router = useRouter();

// -----
// data
// -----
const loading = ref(false);
const showCharts = ref(false);
const chartSelection = ref({} as ExposureMetric);
const exposureData = ref([] as ExposureValue[]);
const exposureTypes = ref([] as ExposureMetric[]);
const exposureCategories = ref([] as Array<LabelValue & { queryValue: string }>);
const selectedCategory = ref(0 as number | undefined);
const showCurrentOnly = ref(false);
const dataSource = ref({} as Record<string, unknown>);
const errorMsg = ref('');
const excelUploadData = ref([] as ExposureValueEntry[]);
const exposureTrendsQueryValue = 'exposure-trends';
const invalidEntryReason = ref('');
const editingEntry = ref(null as ExposureValue | null);
const deletingEntry = ref(null as ExposureValue | null);

const EXPOSURE_ROW_HEIGHT = 44;
const exposureScrollRef = ref<HTMLElement | null>(null);

// ---------
// computed
// ---------
const note = computed(() => {
  if (selectedCategory.value === ExposureCategory.BusinessAuto) {
    return 'Total Units and Annual Miles are NOT required to be entered at the same time.';
  }
  return undefined;
});

const selectedExposureTypes = computed(() => {
  return exposureTypes.value.filter((x) => x.exposureCategoryID === selectedCategory.value);
});

const selectedExposureAttributes = computed(() => {
  const attributes = selectedExposureTypes.value.flatMap((t) => t.exposureAttributes);
  const uniqueAttributesByKey = [...new Map(attributes.map((a) => [a.exposureAttributeKey, a])).values()];
  return uniqueAttributesByKey.sort((a, b) => a.indexPos - b.indexPos);
});

const selectedExposureData = computed(() => {
  const byCategory = exposureData.value.filter((x) => x.categoryID === selectedCategory.value);
  if (showCurrentOnly.value) {
    return (
      exposureTypes.value
        .map((x) => byCategory.filter((y) => y.exposureMetricID === x.exposureMetricID))
        .flatMap((x) => [x.sort((a, b) => CompareISODateStrings(a.effectiveDate, b.effectiveDate))[0]])
        .filter((x) => !!x)
        .sort((a, b) => CompareISODateStrings(a.effectiveDate, b.effectiveDate)) || []
    );
  }
  return byCategory;
});

const hasIEXExposure = computed(() => {
  const now = DateTime.local().startOf('day').toISODate();
  const data = selectedExposureData.value.filter(
    (x) => x.uploadedBy === 'IEX Cloud' && DateTime.fromISO(x.uploadTimeStamp).startOf('day').toISODate() === now,
  );
  return data.length > 0;
});

const canEnter = computed(() => {
  if (!hasIEXExposure.value) {
    return true;
  }
  return selectedExposureTypes.value.length > 0;
});

const canEdit = computed(() => {
  return selectedExposureTypes.value.length > 0;
});

const isUnderConstruction = computed(() => {
  if (selectedCategory.value === ExposureCategory.PropertyLocations) {
    return false;
  }
  return !exposureTypes.value.find((x) => x.exposureCategoryID === selectedCategory.value);
});

const isAdmin = computed(() => {
  return IsUserAnAdmin(store?.state.user);
});

const isAllowed = computed(() => {
  return !IsUserAClient(store?.state.user);
});

const exposureColumnCount = computed(() => {
  const n = 2 + selectedExposureAttributes.value.length + 2 + (isAllowed.value ? 1 : 0) + 1;
  return n;
});

const exposureVirtualizer = useVirtualizer(
  computed(() => ({
    count: selectedExposureData.value.length,
    getScrollElement: () => exposureScrollRef.value,
    estimateSize: () => EXPOSURE_ROW_HEIGHT,
    overscan: 5,
  })),
);

// For table virtualization: visible rows + top/bottom spacer heights so scroll height is correct
const exposureTableVirtual = computed(() => {
  const v = exposureVirtualizer.value;
  const items = v.getVirtualItems();
  const total = v.getTotalSize();
  const topSpacerHeight = items.length ? items[0].start : 0;
  const bottomSpacerHeight = items.length ? total - (items[items.length - 1].start + items[items.length - 1].size) : 0;
  return { items, topSpacerHeight, bottomSpacerHeight };
});

const chartMetricOptions = computed(() => exposureTypes.value.map((t) => ({ value: t.exposureMetricID, label: t.name })));

const chartMetricId = computed({
  get: () => chartSelection.value?.exposureMetricID,
  set: (id: number | undefined) => {
    const o = exposureTypes.value.find((t) => t.exposureMetricID === id);
    if (o) {
      chartSelection.value = o;
      setSelected();
    }
  },
});

// -----------
// lifecycles
// -----------
onBeforeMount(async () => {
  await refreshData();
  routeChanged($route);
});

// ---------
// watchers
// ---------
watch($route, (to: RouteLocation) => {
  routeChanged(to);
});

// ----------
// functions
// ----------
function changeCategory(category: string): void {
  $router.push({ path: $route.path, query: { category } });
}

async function refreshData(): Promise<void> {
  loading.value = true;
  const exposureDataResult = await accountService.getExposures($route.params.id as string);
  const exposureTypesResult = (await referenceService.exposureTypes()).sort((m1, m2) => m1.name.localeCompare(m2.name));

  const exposureOptions = (
    await Promise.all(
      exposureTypesResult
        .flatMap((t) => t.exposureAttributes)
        .filter((a): a is ExposureAttribute & ExposureAttributeWithLookupKey => a && !!a.lookupKey)
        .map((a) => referenceService.exposureOptions(a.lookupKey)),
    )
  )
    .filter((o) => Array.isArray(o) && o.length > 0)
    .reduce((acc, o) => {
      acc.set(o[0].lookupKey, o);
      return acc;
    }, new Map());

  exposureData.value = exposureDataResult
    .map((t) => ({
      ...t,
      exposureAttributes: (t.exposureAttributes || []).map((a) => ({
        ...a,
        options: exposureOptions.get(a.lookupKey),
      })),
    }))
    .sort((a, b) => ('' + b.effectiveDate).localeCompare('' + a.effectiveDate));

  exposureTypes.value = exposureTypesResult.map((t) => ({
    ...t,
    exposureAttributes: (t.exposureAttributes || []).map((a) => ({
      ...a,
      options: exposureOptions.get(a.lookupKey),
    })),
  }));

  const companyType = props.account.ownershipType?.toLowerCase();

  exposureCategories.value = (await referenceService.exposureCategories())
    .filter((c) => {
      const scope = c.companyScope?.toLowerCase();
      return scope === 'all' ? true : scope === companyType;
    })
    .map((c) => ({ ...c, queryValue: c.label.toLowerCase().replaceAll(/[^a-z]/g, '-') }));

  chartSelection.value = exposureTypes.value?.length > 0 ? exposureTypes.value[0] : ({} as ExposureMetric);
  setSelected();

  dispatchEvent(new CustomEvent(Events.REFRESH_ACCOUNT_KPI));
  loading.value = false;
}

async function updateExposureValue(entry: ExposureValueChangeEntry): Promise<void> {
  editingEntry.value = null;
  await accountService.updateExposures($route.params.id as string, [entry]);
  await refreshData();
}

async function insertExposureValues(entries: ExposureValueEntry[]): Promise<void> {
  await accountService.submitExposures($route.params.id as string, entries);
  await refreshData();
}

async function deleteExposureValue(entry: ExposureValue | ExposureProperty): Promise<unknown> {
  await accountService.DeleteExposure('exposureID' in entry ? entry.exposureID : '');
  await refreshData();
  return;
}

async function downloadTable(): Promise<void> {
  const headers = [
    'Exposure Metric',
    'Value',
    ...selectedExposureAttributes.value.map((a) => a.name),
    'Effective Date',
    'Date Uploaded',
    ...(isAllowed.value ? ['Uploaded By'] : []),
  ];
  const escape = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
  const rows = selectedExposureData.value.map((entry) =>
    [
      entry.name ?? '',
      (entry.unitOfMeasure === 'USD' ? '$' : '') + (addCommas(entry.value ?? '') ?? ''),
      ...selectedExposureAttributes.value.map((attr) => attributeValueFor(entry, attr.exposureAttributeKey) ?? '--'),
      filters.date(entry.effectiveDate ?? '') ?? '',
      filters.date(entry.uploadTimeStamp) ?? '',
      ...(isAllowed.value ? [entry.uploadedBy ?? ''] : []),
    ]
      .map(escape)
      .join(','),
  );
  const csv = headers.map(escape).join(',') + '\n' + rows.join('\n');
  DownloadCSV(csv, `${exposureCategories.value.find((x) => x.value === selectedCategory.value)?.label}-${new Date().toISOString().split('T')[0]}`);
}

function validatePayrollFile(file?: File): string[] | undefined {
  if (!file) {
    return ['The file could not be opened'];
  }

  const errors = [] as string[];

  let reader = new FileReader();
  reader.onload = () => {
    try {
      const workbook = XLSX.read(reader.result, { type: 'array' });
      const ws = workbook.Sheets[workbook.SheetNames[0]];
      const range = XLSX.utils.decode_range(ws['!ref'] || '');
      const dataRange: { data: any; address: { c: number; r: number } }[] = [];

      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          let cell_address = { c: C, r: R };
          let data = XLSX.utils.encode_cell(cell_address);
          if (ws[data] && cell_address.r > 3) {
            //excludes headers and blank rows
            dataRange.push({ data: ws[data], address: cell_address });
          }
        }
      }

      const rowMap = dataRange.reduce((acc, cell) => {
        const rowNum = cell.address.r;
        if (!acc.has(rowNum)) acc.set(rowNum, new Map()); // prime the map

        // check for non-empty rows
        if (cell.address.c >= 2 && cell.address.c <= 7 && cell.data.w && cell.data.w.trim() !== '') {
          acc.get(rowNum).set(cell.address.c, cell);
        }
        return acc;
      }, new Map());

      type cell = { data: unknown; address: { c: number; r: number } };
      const requiredCols = new Map([
        [2, 'C'],
        [6, 'G'],
        [7, 'H'],
      ]);

      const entries = Array.from(rowMap.entries()).sort((e1, e2) => e1[0] - e2[0]);
      const rowsWithData = entries.reduce((acc, entry) => {
        const rowValues = entry[1];
        if (rowValues.size > 0) {
          Array.from(requiredCols.entries()).forEach((requiredColIndex) => {
            if (!rowValues.has(requiredColIndex[0])) {
              errors.push(`Cell ${requiredColIndex[1]}${entry[0] + 1} is required`);
            } else {
              acc.push(rowValues);
            }
          });
        }
        return acc;
      }, [] as cell[]);

      if (rowsWithData.length === 0) {
        errors.push('No entries were found to upload');
      }
    } catch (error) {
      console.error(error);
      errorMsg.value = error as string;
    }
    return;
  };
  reader.readAsArrayBuffer(file);
  return errors;
}

async function uploadPayrollFile(file?: File): Promise<string[] | undefined> {
  if (!file) {
    return;
  }

  let reader = new FileReader();
  let opts = (await referenceService.exposureOptions('USPSCode')) as ExposureOption[];
  reader.onload = () => {
    try {
      const workbook = XLSX.read(reader.result, { type: 'array' });
      const ws = workbook.Sheets[workbook.SheetNames[0]];

      const range = XLSX.utils.decode_range(ws['!ref'] || '');
      const dataRange: { data: any; address: { c: number; r: number } }[] = [];

      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          let cell_address = { c: C, r: R };
          let data = XLSX.utils.encode_cell(cell_address);
          //excludes headers and blank rows
          if (ws[data] && cell_address.r > 3) {
            dataRange.push({ data: ws[data], address: cell_address });
          }
        }
      }

      const exposurevalues: Array<ExposureValueEntry> = Array<ExposureValueEntry>();

      const rows = new Set(dataRange.map((x) => x.address.r));
      rows.forEach((element) => {
        if (!dataRange.find((x) => x.address.c === 2 && x.address.r === element)?.data.w) {
          return;
        }

        const stateName = dataRange.find((x) => x.address.c === 3 && x.address.r === element)?.data.w;
        const stateCode = dataRange.find((x) => x.address.c === 4 && x.address.r === element)?.data.w;

        let resolvedState;
        if (stateCode) {
          resolvedState = stateCode;
        } else if (opts && opts.length > 0 && stateName) {
          resolvedState = opts.find((x) => x.text === stateName)?.value;
        }

        const dateParse = DateTime.fromFormat(dataRange.find((x) => x.address.c === 2 && x.address.r === element)?.data.w, 'MM/dd/yy').toString();

        const exposurevalue: ExposureValueEntry = {
          exposureMetricID: selectedExposureTypes.value[0].exposureMetricID,
          effectiveDate: dateParse,
          value: dataRange.find((x) => x.address.c === 7 && x.address.r === element)?.data.w,
          exposureAttributeEntries: [
            { exposureAttributeID: 1, value: resolvedState },
            { exposureAttributeID: 2, value: dataRange.find((x) => x.address.c === 5 && x.address.r === element)?.data.w },
            { exposureAttributeID: 3, value: dataRange.find((x) => x.address.c === 6 && x.address.r === element)?.data.w },
          ],
        };
        if (resolvedState && resolvedState.length === 2 && dateParse) {
          exposurevalues.push(exposurevalue);
        }
      });

      excelUploadData.value = exposurevalues;
    } catch (error) {
      console.error(error);
      errorMsg.value = error as string;
    }
    return;
  };
  reader.readAsArrayBuffer(file);
}

function attributeValueFor(entry: ExposureValue, key: string): string | undefined {
  const attr = entry.exposureAttributes.find((a) => a.exposureAttributeKey === key);
  if (attr) {
    return attr.value;
  }
  return undefined;
}

function attributeValueIdFor(entry: ExposureValue, key: string): string | undefined {
  const attr = entry.exposureAttributes.find((a) => a.exposureAttributeKey === key);
  if (attr) {
    return '' + attr.exposureAttributeValueId;
  }
  return undefined;
}

function canTakeAction(data: ExposureValue): boolean {
  return data.systemCreated ? isAdmin.value : isAllowed.value;
}

function setSelected() {
  const selectedMetricData = exposureData.value
    .filter((d) => d.exposureMetricID === chartSelection.value.exposureMetricID)
    .sort((a: ExposureValue, b: ExposureValue) => CompareISODateStrings(a.effectiveDate, b.effectiveDate, false));

  /*
         1. in date range get Jan 1 of the earliest date, and Dec 31 of the last date + 1 month (for some extra buffer)
         2. create bins for each month in the range; using an interval of 12 (12 months in 1 year) to make math easy
       */

  const ds = {
    chart: {
      ...(trendChart.chart as Record<string, unknown>),
      caption: chartSelection.value.name,
      ynumberprefix: chartSelection.value.unitOfMeasure === 'USD' ? '$' : '',
    },
  };

  if (selectedMetricData.length < 2) {
    dataSource.value = ds;
    return;
  }

  let date1 = DateTime.fromISO(selectedMetricData[0].effectiveDate ?? '').set({ month: 1, day: 1 });
  let date2 = DateTime.fromISO(selectedMetricData[selectedMetricData.length - 1].effectiveDate ?? '')
    .set({ month: 12, day: 31 })
    .plus({ month: 1 });

  const dateIntervals: { x: number; label: string; showverticalline: string }[] = [];
  const intervalMap = new Map();
  for (let i = 1; date1 < date2; date1 = date1.plus({ year: 1 }), i += 12) {
    intervalMap.set(date1.year, i);
    dateIntervals.push({
      x: i,
      label: `${date1.year}`,
      showverticalline: i === 1 ? '0' : '1',
    });
  }

  const groupedData = selectedMetricData.reduce((acc, d) => {
    const dt = DateTime.fromISO(d.effectiveDate ?? '', { zone: 'utc' } as DateTimeOptions);
    const key = dt.toFormat('yyyy-MM');

    if (!acc.has(key)) {
      acc.set(key, []);
    }
    acc.get(key).push({ ...d, xval: intervalMap.get(dt.year) + dt.month - 1, effectiveDate: dt });

    return acc;
  }, new Map());

  const data: any = [];

  groupedData.forEach((value) => {
    let y;
    switch (chartSelection.value.name) {
      case 'Payroll':
        y = value.reduce((max: number, val: Record<string, any>) => max + val.value, 0);
        break;
      default:
        y = value.reduce((max: number, val: Record<string, any>) => Math.max(max, val.value), 0);
    }
    data.push({ x: value.length > 0 ? value[value.length - 1].xval : 0, y, showValue: 1 });
  });

  dataSource.value = {
    chart: {
      ...(trendChart.chart as Record<string, unknown>),
      caption: chartSelection.value.name,
      ynumberprefix: chartSelection.value.unitOfMeasure === 'USD' ? '$' : '',
      decimals: chartSelection.value.exposureCategoryID === 2 ? '0' : '',
    },
    categories: [{ category: dateIntervals }],
    dataset: [{ drawLine: '1', data: data }],
  };
}

async function uploadedPayroll() {
  excelUploadData.value = [];
  await refreshData();
}

function clearUpload() {
  excelUploadData.value = [];
}

function routeChanged(to: RouteLocation) {
  const queryCategory = to.query.category;
  if (queryCategory && typeof queryCategory === 'string') {
    if (queryCategory === exposureTrendsQueryValue) {
      selectedCategory.value = -1;
      showCharts.value = true;
      return;
    }
    const selected = exposureCategories.value.find((x) => x.queryValue === queryCategory);
    if (selected) {
      selectedCategory.value = selected.value;
      showCharts.value = false;
      return;
    }
  }
  selectedCategory.value = exposureCategories.value[0].value;
  showCharts.value = false;
}
</script>

<template>
  <div class="dashboard">
    <div
      v-if="loading"
      class="space-y-2 p-2">
      <Skeleton
        v-for="i in 8"
        :key="i"
        shape="rectangle"
        class="w-full"
        height="44px" />
    </div>
    <div
      class="inner"
      v-if="!loading">
      <div class="mt-2 flex flex-row">
        <div class="flex flex-[0_1_auto] flex-col gap-3 p-2">
          <AppButton
            :primary="selectedCategory === -1"
            @click="changeCategory(exposureTrendsQueryValue)">
            Exposure Trends
          </AppButton>
          <hr class="my-2 border-gray-200" />
          <AppButton
            v-for="category in exposureCategories"
            :key="category.value"
            :primary="selectedCategory === category.value"
            :data-testid="`category-${category.value}`"
            :data-selected="selectedCategory === category.value ? 'true' : 'false'"
            @click="changeCategory(category.queryValue)">
            {{ category.label }}
          </AppButton>
        </div>
        <div
          v-if="showCharts"
          class="ml-4 max-w-none min-w-0 flex-1">
          <div class="flex flex-col gap-4">
            <div>
              <AppSelect
                v-model="chartMetricId"
                :options="chartMetricOptions"
                class="w-80"
                data-testid="exposure-metric-list"
                placeholder="Select metric" />
            </div>
            <div>
              <fusioncharts
                type="scatter"
                width="700"
                height="400"
                dataFormat="json"
                :dataSource="dataSource"
                dataEmptyMessage="Insufficient data (no trend available)."
                data-testid="exposure-trend-chart">
              </fusioncharts>
            </div>
          </div>
        </div>
        <div
          v-if="selectedCategory === ExposureCategory.PropertyLocations"
          class="ml-4 max-w-none min-w-0 flex-1">
          <AccountExposureProperties />
        </div>
        <div
          v-else-if="!isUnderConstruction && !showCharts"
          class="ml-4 max-w-none min-w-0 flex-1">
          <div class="flex flex-col gap-4">
            <div class="mb-4 flex justify-between">
              <h2>
                Exposure Data
                <download-button
                  data-testid="download-btn"
                  @click="downloadTable" />
              </h2>
              <PayrollUploadModal
                :dirtyExposures="excelUploadData"
                @uploadevent="uploadedPayroll"
                @cancelevent="clearUpload"
                v-if="excelUploadData.length > 0" />
              <div class="grid grid-cols-[auto_1fr]">
                <toggle
                  id="show-current"
                  v-model="showCurrentOnly"
                  label="Show Current Only" />

                <FileUploadModal
                  v-if="selectedCategory === ExposureCategory.Payroll"
                  title="Upload Payroll Data From Template"
                  subtitle="Use the template below to upload payroll data."
                  confirm-text="Next"
                  template-icon="mdi-microsoft-excel"
                  template-name="Payroll Template"
                  template-path="/downloads/payrolltemplate.xlsx"
                  :upload-action="uploadPayrollFile"
                  :validate="validatePayrollFile">
                  <template v-slot:buttonActivator="{ props }">
                    <AppButton
                      primary
                      class="mx-3 mt-1 mb-1"
                      data-test="upload-payroll-button"
                      v-bind="props">
                      Upload Payroll
                      <Plus
                        :size="16"
                        class="ml-1" />
                    </AppButton>
                  </template>
                </FileUploadModal>
              </div>
            </div>

            <div
              id="exposure-data"
              class="apptable sortable dense w-full min-w-0 overflow-x-hidden border-t border-b border-[#e6e6e6]">
              <div
                ref="exposureScrollRef"
                class="max-h-[680px] min-w-0 overflow-x-hidden overflow-y-auto">
                <table class="exposure-table w-full table-fixed border-collapse">
                  <thead class="exposure-thead">
                    <tr>
                      <th class="border-none p-2 text-left">Exposure Metric</th>
                      <th class="money border-none p-2 text-left">Value</th>
                      <th
                        v-for="(attr, index) in selectedExposureAttributes"
                        :key="`h-attr-${index}`"
                        class="border-none p-2 text-left">
                        {{ attr.name }}
                      </th>
                      <th class="date sorted border-none p-2 text-left">Effective Date</th>
                      <th class="date border-none p-2 text-left">Date Uploaded</th>
                      <th
                        v-if="isAllowed"
                        class="border-none p-2 text-left">
                        Uploaded By
                      </th>
                      <th class="actions cursor-context-menu border-none p-2 text-right whitespace-nowrap"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="exposureTableVirtual.topSpacerHeight > 0">
                      <td
                        :colspan="exposureColumnCount"
                        class="exposure-spacer border-none p-0 align-top leading-none"
                        :style="{ height: exposureTableVirtual.topSpacerHeight + 'px' }" />
                    </tr>
                    <tr
                      v-for="virtualRow in exposureTableVirtual.items"
                      :key="String(virtualRow.key)"
                      class="exposure-table-body-row group border-t border-[#e6e6e6]">
                      <template v-if="selectedExposureData[virtualRow.index]">
                        <td class="min-w-0 overflow-hidden p-2 text-left group-hover:bg-[#f5f8fc]">
                          {{ selectedExposureData[virtualRow.index].name }}
                        </td>
                        <td
                          class="money min-w-0 overflow-hidden p-2 text-left group-hover:bg-[#f5f8fc]"
                          :data-testid="`exp-value-${selectedExposureData[virtualRow.index].exposureID}`">
                          {{ selectedExposureData[virtualRow.index].unitOfMeasure === 'USD' ? `$` : ''
                          }}{{ addCommas(selectedExposureData[virtualRow.index].value ?? '') }}
                        </td>
                        <td
                          v-for="attr in selectedExposureAttributes"
                          :key="attr.exposureAttributeKey"
                          class="min-w-0 overflow-hidden p-2 text-left group-hover:bg-[#f5f8fc]"
                          :data-testid="`attr-value-${attributeValueIdFor(selectedExposureData[virtualRow.index], attr.exposureAttributeKey)}`">
                          {{ attributeValueFor(selectedExposureData[virtualRow.index], attr.exposureAttributeKey) ?? '--' }}
                        </td>
                        <td class="date sorted min-w-0 overflow-hidden p-2 text-left group-hover:bg-[#f5f8fc]">
                          {{ filters.date(selectedExposureData[virtualRow.index].effectiveDate ?? '') }}
                        </td>
                        <td class="date min-w-0 overflow-hidden p-2 text-left group-hover:bg-[#f5f8fc]">
                          {{ filters.date(selectedExposureData[virtualRow.index].uploadTimeStamp) }}
                        </td>
                        <td
                          v-if="isAllowed"
                          class="min-w-0 overflow-hidden p-2 text-left group-hover:bg-[#f5f8fc]">
                          {{ selectedExposureData[virtualRow.index].uploadedBy }}
                        </td>
                        <td
                          class="actions min-w-0 overflow-hidden p-2 text-right whitespace-nowrap select-none group-hover:bg-[#f5f8fc]">
                          <div
                            class="actions-div inline-flex items-center gap-1 pr-2"
                            data-testid="actions-div">
                            <button
                              v-if="canTakeAction(selectedExposureData[virtualRow.index])"
                              type="button"
                              class="exposure-action-icon inline-flex cursor-pointer rounded p-1 opacity-30 group-hover:opacity-100"
                              data-testid="delete-btn"
                              title="Delete"
                              @click="deletingEntry = selectedExposureData[virtualRow.index]">
                              <Trash2 :size="16" />
                            </button>
                            <button
                              v-if="canTakeAction(selectedExposureData[virtualRow.index]) && canEdit"
                              type="button"
                              class="exposure-action-icon inline-flex cursor-pointer rounded p-1 opacity-30 group-hover:opacity-100"
                              data-testid="edit-btn"
                              title="Edit"
                              @click="editingEntry = selectedExposureData[virtualRow.index]">
                              <Edit2 :size="16" />
                            </button>
                          </div>
                        </td>
                      </template>
                    </tr>
                    <tr v-if="exposureTableVirtual.bottomSpacerHeight > 0">
                      <td
                        :colspan="exposureColumnCount"
                        class="exposure-spacer border-none p-0 align-top leading-none"
                        :style="{ height: exposureTableVirtual.bottomSpacerHeight + 'px' }" />
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <AccountExposureEditModal
            v-if="editingEntry"
            :exposure-entry="editingEntry"
            :update="updateExposureValue"
            :can-edit="canEdit"
            :show="true"
            @closed="editingEntry = null" />
          <AccountExposureDeleteModal
            v-if="deletingEntry"
            :exposure="deletingEntry"
            :delete="deleteExposureValue"
            :show="true"
            @closed="deletingEntry = null" />
          <div class="flex justify-end">
            <AccountExposureEntryModal
              :note="note"
              :exposureTypes="selectedExposureTypes"
              :canEnter="canEnter"
              :insert="insertExposureValues"
              :invalidEntryReason="invalidEntryReason"
              :exposureData="exposureData" />
          </div>
        </div>
        <div
          v-else-if="isUnderConstruction"
          class="ml-4 max-w-none min-w-0 flex-1">
          <div class="text-center">
            <h2>Under Construction</h2>
            <p>Check back soon for updates.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Table header: sticky + apptable styling */
.exposure-thead {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #4d9cb9;
  color: white;
}

.exposure-thead th {
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  text-transform: uppercase;
}

/* Spacer rows for virtual scroll: no padding/border so height is exact */
td.exposure-spacer {
  padding: 0 !important;
  border: none !important;
  line-height: 0;
  vertical-align: top;
}

/* Action icon hover + separator (::after) and coarse-pointer - not easily done in Tailwind */
.exposure-table-body-row > td.actions .actions-div .exposure-action-icon {
  transition:
    opacity 0.15s ease,
    background-color 0.15s ease;
}

.exposure-table-body-row > td.actions .actions-div .exposure-action-icon:hover {
  background-color: rgba(0, 0, 0, 0.1);
  opacity: 1;
}

.exposure-table-body-row > td.actions .actions-div::after {
  content: '|';
  color: #cccddd;
  padding-left: 0.5rem;
}

.exposure-table-body-row > td.actions .actions-div:last-of-type::after {
  display: none;
}

@media (pointer: none), (pointer: coarse) {
  .exposure-table-body-row > td.actions .actions-div button {
    opacity: 1;
  }
}
</style>
